// -----------------------------------------------------------------------------
// Typing indicators over the chat SignalR hub.
//
// `useChatTypingIndicator(channelId, meId)` returns the ids of other users
// currently typing in the given channel. The hub's `UserTyping` event is
// fire-and-forget (nothing persisted server-side), so each signal is aged
// out locally after a few seconds of silence.
//
// `useSendTypingSignal(channelId, meId)` returns a throttled callback the
// composer calls on every keystroke — it invokes `Typing` on the hub at
// most once every couple of seconds so a fast typist doesn't flood the
// connection.
// -----------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";

import { useSignalR } from "../contexts/SignalRContext";

const TYPING_TTL_MS = 4000;
const TYPING_THROTTLE_MS = 2000;

export function useChatTypingIndicator(
  channelId: string | null,
  meId: string | null
): string[] {
  const { chatConnection } = useSignalR();
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);
  // userId -> last signal timestamp. A ref so the SignalR handler and the
  // pruning interval share state without re-subscribing on every signal.
  const lastSeenRef = useRef<Map<string, number>>(new Map());

  // Reset when switching channels so stale names don't flash up.
  useEffect(() => {
    lastSeenRef.current.clear();
    setTypingUserIds([]);
  }, [channelId]);

  useEffect(() => {
    if (!chatConnection || !channelId) return;

    const onTyping = (eventChannelId: string, userId: string) => {
      if (eventChannelId !== channelId) return;
      if (meId && userId === meId) return;
      lastSeenRef.current.set(userId, Date.now());
      setTypingUserIds((current) =>
        current.includes(userId) ? current : [...current, userId]
      );
    };

    // Prune signals that have gone quiet. Runs on a coarse interval —
    // the indicator disappearing ~a second late is imperceptible.
    const prune = window.setInterval(() => {
      const cutoff = Date.now() - TYPING_TTL_MS;
      let changed = false;
      lastSeenRef.current.forEach((seenAt, userId) => {
        if (seenAt < cutoff) {
          lastSeenRef.current.delete(userId);
          changed = true;
        }
      });
      if (changed) {
        setTypingUserIds(Array.from(lastSeenRef.current.keys()));
      }
    }, 1000);

    chatConnection.on("UserTyping", onTyping);
    return () => {
      chatConnection.off("UserTyping", onTyping);
      window.clearInterval(prune);
    };
  }, [chatConnection, channelId, meId]);

  return typingUserIds;
}

export function useSendTypingSignal(
  channelId: string | null,
  meId: string | null
): () => void {
  const { chatConnection } = useSignalR();
  const lastSentRef = useRef(0);

  return useCallback(() => {
    if (!chatConnection || !channelId || !meId) return;
    const now = Date.now();
    if (now - lastSentRef.current < TYPING_THROTTLE_MS) return;
    lastSentRef.current = now;
    chatConnection.invoke("Typing", channelId, meId).catch(() => {
      /* best-effort - a dropped typing signal is harmless */
    });
  }, [chatConnection, channelId, meId]);
}
